import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule, ValidationErrors, AbstractControl, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthenticationService } from '../authentication.service';
import { WarningPanelComponent } from '../../comps/warning-panel/warning-panel.component';
import { SimpleProgressComponent } from '../../comps/simpleprogress.component';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { BGAnimationComponent } from '../../comps/bganimation/bganimation.component';
import { AuthShellComponent } from '../auth-shell/auth-shell.component';
import { environment } from '../../../environments/environment';

// Custom validator to check if password and confirm password match
export function passwordMatchValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const password = control.get('password');
    const confirmPassword = control.get('confirmpassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }
    return null;
  };
}

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    imports: [
        BGAnimationComponent,
        AuthShellComponent,
        FormsModule,
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        RouterLink,
        SimpleProgressComponent,
        WarningPanelComponent,
    ]
})
export class ResetPasswordComponent {

  environment = environment;

  form: UntypedFormGroup;
  error: string;
  public busy = false;
  message: string = null;
  resetDone = false;

  // True once a ccode query param has been seen - i.e. the user followed the
  // emailed reset link. Tracked separately from the code form control's
  // validity so a link that's missing the user param (e.g. #1676 CustomMessage
  // Lambda bug where it sent "user=undefined") can show a distinct error
  // state instead of silently falling back to the "request a code" view.
  linkCodePresent = false;

  private email: string;
  private code: string;

  private fb = inject(UntypedFormBuilder);
  private authService = inject(AuthenticationService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  constructor() {
    const route = inject(ActivatedRoute);
    this.createForm();

    route.queryParamMap.subscribe(
      params => {
        const userParam = params.get('user');
        // Treat a literal "undefined" as absent too, in case a stale email
        // sent before the CustomMessage Lambda fix is still in someone's inbox.
        if (userParam && userParam !== 'undefined') {
          // an unencoded '+' in the emailed link arrives as a space; addresses can't contain spaces
          this.email = userParam.replace(/ /g, '+');
          this.form.controls.email.setValue(this.email);
        }
        const ccodeParam = params.get('ccode');
        if (ccodeParam) {
          this.code = ccodeParam;
          this.form.controls.code.setValue(this.code);
        }
        this.linkCodePresent = !!ccodeParam;
        this.cdr.markForCheck();
      });
  }

  requestNewCode(): void {
    this.form.controls.code.setValue('');
    this.form.controls.email.setValue('');
    this.linkCodePresent = false;
    this.error = '';
    this.message = null;
    this.router.navigateByUrl('/resetpass');
    this.cdr.markForCheck();
  }

  async getcode() {

    this.error = '';
    this.message = null;
    this.busy = true;

    // this.changeDetectorRef.detectChanges();

    if (this.form.controls.email.valid) {

      try {
        await this.authService.forgotPassword(
          this.form.controls.email.value
        ).then(result => {

          if (result) {
            this.message = 'Reset code was sent via email';
            this.router.navigateByUrl('/resetpass');
            this.cdr.markForCheck();
          }

        }).catch(err => {
          this.error = err.message;

          if (err.name === 'UserNotFoundException') {
            this.error = 'Email, Code or Password are invalid.';
          }
          this.cdr.markForCheck();
        });

      } finally {
        this.busy = false;
        this.cdr.markForCheck();
      }
    }
  }

  async reset() {
    this.error = '';
    this.busy = false;
    this.message = null;

    if (this.form.valid) {
      this.busy = true;

      try {
        await this.authService.forgotPasswordUpdate(
          this.form.controls.email.value,
          this.form.controls.code.value,
          this.form.controls.password.value
        ).then(result => {

          if (result) {
            this.resetDone = true;
            this.message = 'Password Reset OK. Try to Login.';
            this.cdr.markForCheck();
          }

        }).catch(err => {
          this.error = 'Invalid email or code entered. Check the input or get a new code.'; //err.message;
          this.cdr.markForCheck();
        });

      } finally {
        this.busy = false;
        this.cdr.markForCheck();
      }
    }
  }

  private createForm(): void {
    this.form = this.fb.group({
      email: new UntypedFormControl(this.email || '', Validators.required),
      code: new UntypedFormControl(this.code || '', Validators.required),
      password: new UntypedFormControl('', Validators.required),
      confirmpassword: new UntypedFormControl('', Validators.required)
    }, { validators: passwordMatchValidator() });
  }

}
